import { DateTime } from "luxon"
import React, { useRef, useState, useEffect, useCallback, useMemo, useLayoutEffect } from "react"

import { PRIMARY_BLUE } from "@/constants/colors"
import { RESERVATION_COLORS, type ReservationStatus } from "@/constants/reservations"
import { SLOT_MINUTES } from "@/constants/ui"
import { useDragAutoScroll } from "@/shared/use_drag_auto_scroll"

import { clamp, normalizeTime } from "../helpers/time_math"
import { useTimeSlots } from "../hooks/use_time_slots"

import SlotCell from "./slot_cell"
import TimeAxis from "./time_axis"

interface BusyBlock {
  startTime: string
  endTime: string
}

interface CurrentReservation {
  startTime: string | DateTime
  endTime: string | DateTime
  status?: ReservationStatus
}

interface HorizontalScheduleProps {
  timeOptions: [string, DateTime][]
  busyBlocks?: BusyBlock[]
  currentReservation?: CurrentReservation
  closedUntil?: DateTime | null
  onTimeChange?: (startTime: DateTime, endTime: DateTime) => void
  onConflictChange?: (hasConflicts: boolean) => void
  reservationBuffer?: number
  disabled?: boolean
  scrollToStartTime?: DateTime | null
  scrollToEndTime?: DateTime | null
  showFloatingTimeLabels?: boolean
  headerContent?: React.ReactNode
}

type FloatingTimeLabelLayout =
  | {
      mode: "separate"
      startLeft: number
      endLeft: number
    }
  | {
      mode: "grouped"
      groupLeft: number
    }

type SlotMetrics = {
  index: number
  left: number
  right: number
  center: number
}

const FLOATING_TIME_LABEL_GAP = 8
const FALLBACK_FLOATING_TIME_LABEL_WIDTH = 32

const STYLES = {
  container: "w-full bg-white border-t border-[#e5e6ec] overflow-hidden",
  headerContent: "px-5 pt-3 pb-1",
  scrollContainer: "overflow-x-auto",
  slotsContainer: "relative flex items-start",
  floatingTimeLabelRow: "pointer-events-none absolute -top-6 left-0 z-10 h-0 w-full",
  floatingTimeGrouped:
    "absolute top-0 flex items-center gap-2 whitespace-nowrap text-[10px] font-medium text-[#2596fa]",
  floatingTimeSeparate: "absolute top-0 whitespace-nowrap text-[10px] font-medium text-[#2596fa]",
  floatingTimeMeasure:
    "pointer-events-none invisible absolute -top-6 left-0 h-0 overflow-hidden whitespace-nowrap text-[10px] font-medium text-[#2596fa]",
} as const

const SLOT_COLORS = {
  conflict: "#e8787a",
  selection: PRIMARY_BLUE,
  busy: "#a0b8d0",
  closed: "#d1d5db",
  default: "#F5F5F5",
} as const

const HorizontalSchedule: React.FC<HorizontalScheduleProps> = ({
  timeOptions,
  busyBlocks = [],
  currentReservation,
  closedUntil,
  onTimeChange,
  onConflictChange,
  reservationBuffer: _reservationBuffer = 0,
  disabled = false,
  scrollToStartTime = null,
  scrollToEndTime = null,
  showFloatingTimeLabels = false,
  headerContent,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const slotsContainerRef = useRef<HTMLDivElement>(null)
  const startLabelMeasureRef = useRef<HTMLSpanElement>(null)
  const endLabelMeasureRef = useRef<HTMLSpanElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartSlot, setDragStartSlot] = useState<DateTime | null>(null)
  const [dragEndSlot, setDragEndSlot] = useState<DateTime | null>(null)
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null)
  const [isScrolling, setIsScrolling] = useState(false)
  const [floatingTimeLabelLayout, setFloatingTimeLabelLayout] = useState<FloatingTimeLabelLayout | null>(null)
  const dragStartRef = useRef<DateTime | null>(null)
  const dragEndRef = useRef<DateTime | null>(null)
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const slotMetricsRef = useRef<SlotMetrics[]>([])

  useDragAutoScroll({
    scrollContainerRef,
    isDragging,
    clientX: mousePosition?.x ?? null,
    clientY: mousePosition?.y ?? null,
    options: { direction: "horizontal", threshold: 60, maxSpeed: 15 },
  })

  const slots = useMemo(() => timeOptions.map(([_, dt]) => dt), [timeOptions])

  const currentResStart = useMemo(
    () => (currentReservation ? normalizeTime(currentReservation.startTime) : null),
    [currentReservation?.startTime],
  )
  const currentResEnd = useMemo(
    () => (currentReservation ? normalizeTime(currentReservation.endTime) : null),
    [currentReservation?.endTime],
  )

  useEffect(() => {
    if (!scrollToStartTime || !scrollToEndTime || !scrollContainerRef.current) return

    const container = scrollContainerRef.current
    const slotsContainer = container.querySelector("[data-slots-container]")
    if (!slotsContainer || slots.length === 0) return

    const startTimeMs = scrollToStartTime.toMillis()
    const endTimeMs = scrollToEndTime.toMillis()
    const startIndex = slots.findIndex((slot) => slot.toMillis() === startTimeMs)
    const endIndex = slots.findIndex((slot) => slot.toMillis() >= endTimeMs)

    if (startIndex === -1) return

    const startEl = slotsContainer.querySelector(`[data-slot-index="${startIndex}"]`)
    const endEl =
      endIndex !== -1
        ? slotsContainer.querySelector(`[data-slot-index="${endIndex}"]`)
        : slotsContainer.querySelector(`[data-slot-index="${slots.length - 1}"]`)

    if (!startEl || !endEl) return

    const containerRect = container.getBoundingClientRect()
    const startRect = startEl.getBoundingClientRect()
    const endRect = endEl.getBoundingClientRect()
    const blockCenter = (startRect.left + endRect.right) / 2 - containerRect.left + container.scrollLeft
    const targetScrollLeft = blockCenter - container.clientWidth / 2

    container.scrollTo({
      left: Math.max(0, targetScrollLeft),
      behavior: "smooth",
    })
  }, [scrollToStartTime, scrollToEndTime, slots])

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    const handleScroll = () => {
      setIsScrolling(true)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false)
      }, 1000)
    }

    scrollContainer.addEventListener("scroll", handleScroll)
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  const { conflicts, conflictRange, selectionRange } = useTimeSlots({
    slots,
    dragStartSlot,
    dragEndSlot,
    currentResStart,
    currentResEnd,
    busyBlocks,
    closedUntil,
  })

  const updateSlotMetrics = useCallback(() => {
    const slotsContainer = slotsContainerRef.current
    if (!slotsContainer) {
      slotMetricsRef.current = []
      return
    }

    slotMetricsRef.current = Array.from(slotsContainer.querySelectorAll<HTMLElement>("[data-slot-index]")).map(
      (slotEl) => {
        const index = parseInt(slotEl.getAttribute("data-slot-index") || "0", 10)
        const left = slotEl.offsetLeft
        const right = left + slotEl.offsetWidth

        return {
          index,
          left,
          right,
          center: left + slotEl.offsetWidth / 2,
        }
      },
    )
  }, [])

  useLayoutEffect(() => {
    updateSlotMetrics()

    const slotsContainer = slotsContainerRef.current
    if (!slotsContainer || typeof ResizeObserver === "undefined") return

    const resizeObserver = new ResizeObserver(updateSlotMetrics)
    resizeObserver.observe(slotsContainer)

    return () => {
      resizeObserver.disconnect()
    }
  }, [slots, updateSlotMetrics])

  const getSlotFromX = useCallback(
    (x: number): DateTime | null => {
      const slotsContainer = slotsContainerRef.current
      const scrollContainer = scrollContainerRef.current
      const metrics = slotMetricsRef.current
      if (!slotsContainer || !scrollContainer || metrics.length === 0) return null

      const contentX = x - slotsContainer.getBoundingClientRect().left + scrollContainer.scrollLeft
      const directMatch = metrics.find((slotMetric) => contentX >= slotMetric.left && contentX <= slotMetric.right)
      const resolvedMetric =
        directMatch ||
        metrics.reduce((nearestMetric, slotMetric) =>
          Math.abs(slotMetric.center - contentX) < Math.abs(nearestMetric.center - contentX)
            ? slotMetric
            : nearestMetric,
        )
      const index = clamp(resolvedMetric.index, 0, slots.length - 1)

      return slots[index] ?? null
    },
    [slots],
  )

  useEffect(() => {
    if (!isDragging) return

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY
      setMousePosition({ x: clientX, y: clientY })
      const slot = getSlotFromX(clientX)
      if (slot) {
        const endSlot = slot.plus({ minutes: SLOT_MINUTES })
        setDragEndSlot(endSlot)
        dragEndRef.current = endSlot
      }
    }

    const handleUp = () => {
      const start = dragStartRef.current
      const end = dragEndRef.current

      if (start && end && onTimeChange) {
        const finalStart = start < end ? start : end
        const finalEnd = start < end ? end : start
        onTimeChange(finalStart, finalEnd)
      }

      setIsDragging(false)
      setDragStartSlot(null)
      setDragEndSlot(null)
      setMousePosition(null)
      dragStartRef.current = null
      dragEndRef.current = null
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsDragging(false)
        setDragStartSlot(null)
        setDragEndSlot(null)
        setMousePosition(null)
        dragStartRef.current = null
        dragEndRef.current = null
      }
    }

    window.addEventListener("mousemove", handleMove)
    window.addEventListener("touchmove", handleMove)
    window.addEventListener("mouseup", handleUp)
    window.addEventListener("touchend", handleUp)
    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("mousemove", handleMove)
      window.removeEventListener("touchmove", handleMove)
      window.removeEventListener("mouseup", handleUp)
      window.removeEventListener("touchend", handleUp)
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [isDragging, getSlotFromX, onTimeChange])

  useEffect(() => {
    if (!showFloatingTimeLabels || !selectionRange || !slotsContainerRef.current) {
      setFloatingTimeLabelLayout(null)
      return
    }

    const updateFloatingTimeLabelLayout = () => {
      const slotsContainer = slotsContainerRef.current
      if (!slotsContainer) return

      const startSlot = slotsContainer.querySelector<HTMLElement>(`[data-slot-index="${selectionRange.startIndex}"]`)
      const endBoundarySlot =
        selectionRange.endBoundaryIndex < slots.length
          ? slotsContainer.querySelector<HTMLElement>(`[data-slot-index="${selectionRange.endBoundaryIndex}"]`)
          : null
      const lastSlot = slotsContainer.querySelector<HTMLElement>(`[data-slot-index="${slots.length - 1}"]`)

      if (!startSlot || !lastSlot) return

      const startEdge = startSlot.offsetLeft
      const endEdge = endBoundarySlot ? endBoundarySlot.offsetLeft : lastSlot.offsetLeft + lastSlot.offsetWidth
      const containerWidth = slotsContainer.clientWidth
      const startLabelWidth = startLabelMeasureRef.current?.offsetWidth ?? FALLBACK_FLOATING_TIME_LABEL_WIDTH
      const endLabelWidth = endLabelMeasureRef.current?.offsetWidth ?? FALLBACK_FLOATING_TIME_LABEL_WIDTH
      const startLeft = clamp(startEdge, 0, Math.max(containerWidth - startLabelWidth, 0))
      const endLeft = clamp(endEdge - endLabelWidth, 0, Math.max(containerWidth - endLabelWidth, 0))

      if (endLeft - (startLeft + startLabelWidth) >= FLOATING_TIME_LABEL_GAP) {
        setFloatingTimeLabelLayout({ mode: "separate", startLeft, endLeft })
        return
      }

      const groupedWidth = startLabelWidth + FLOATING_TIME_LABEL_GAP + endLabelWidth
      const groupedCenter = (startEdge + endEdge) / 2
      const groupLeft = clamp(groupedCenter - groupedWidth / 2, 0, Math.max(containerWidth - groupedWidth, 0))

      setFloatingTimeLabelLayout({ mode: "grouped", groupLeft })
    }

    updateFloatingTimeLabelLayout()

    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateFloatingTimeLabelLayout)

    resizeObserver?.observe(slotsContainerRef.current)

    if (startLabelMeasureRef.current) {
      resizeObserver?.observe(startLabelMeasureRef.current)
    }

    if (endLabelMeasureRef.current) {
      resizeObserver?.observe(endLabelMeasureRef.current)
    }

    window.addEventListener("resize", updateFloatingTimeLabelLayout)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener("resize", updateFloatingTimeLabelLayout)
    }
  }, [showFloatingTimeLabels, selectionRange, slots.length])

  useEffect(() => {
    onConflictChange?.(conflicts.length > 0)
  }, [conflicts.length, onConflictChange])

  if (timeOptions.length === 0) return null

  const isSlotInCurrentReservation = (slot: DateTime): boolean => {
    if (!currentResStart || !currentResEnd) return false
    return slot >= currentResStart && slot < currentResEnd
  }

  const isSlotInBusyBlock = (slot: DateTime): boolean => {
    return busyBlocks.some((block) => {
      const blockStart = DateTime.fromISO(block.startTime)
      const blockEnd = DateTime.fromISO(block.endTime)
      return slot >= blockStart && slot < blockEnd
    })
  }

  const isSlotClosed = (slot: DateTime): boolean => {
    if (!closedUntil) return false
    return slot < closedUntil
  }

  const isSlotInSelection = (slot: DateTime): boolean => {
    if (!dragStartSlot || !dragEndSlot) return false
    const start = dragStartSlot < dragEndSlot ? dragStartSlot : dragEndSlot
    const end = dragStartSlot < dragEndSlot ? dragEndSlot : dragStartSlot
    return slot >= start && slot < end
  }

  const getSlotColor = (slot: DateTime, hasConflict: boolean): string => {
    if (hasConflict) return SLOT_COLORS.conflict
    if (isSlotInSelection(slot)) return SLOT_COLORS.selection
    if (isSlotInCurrentReservation(slot)) {
      const status = currentReservation?.status || "confirmed"
      return RESERVATION_COLORS[status]?.main || SLOT_COLORS.selection
    }
    if (isSlotInBusyBlock(slot)) return SLOT_COLORS.busy
    if (isSlotClosed(slot)) return SLOT_COLORS.closed
    return SLOT_COLORS.default
  }

  const getSlotOpacity = (slot: DateTime): number => {
    if (isSlotInSelection(slot) && isDragging) return 0.5
    return 1
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled) return
    e.preventDefault()
    const slot = getSlotFromX(e.clientX)
    if (slot) {
      setIsDragging(true)
      setDragStartSlot(slot)
      setDragEndSlot(slot.plus({ minutes: SLOT_MINUTES }))
      setMousePosition({ x: e.clientX, y: e.clientY })
      dragStartRef.current = slot
      dragEndRef.current = slot.plus({ minutes: SLOT_MINUTES })
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (disabled) return
    const touch = e.touches[0]
    const slot = getSlotFromX(touch.clientX)
    if (slot) {
      setIsDragging(true)
      setDragStartSlot(slot)
      setDragEndSlot(slot.plus({ minutes: SLOT_MINUTES }))
      setMousePosition({ x: touch.clientX, y: touch.clientY })
      dragStartRef.current = slot
      dragEndRef.current = slot.plus({ minutes: SLOT_MINUTES })
    }
  }

  const trailingTime = slots[slots.length - 1].plus({ minutes: SLOT_MINUTES })

  return (
    <div ref={containerRef} className={STYLES.container} data-testid="schedule-container">
      {headerContent ? <div className={STYLES.headerContent}>{headerContent}</div> : null}
      <div ref={scrollContainerRef} className={STYLES.scrollContainer}>
        <div
          className={
            headerContent
              ? showFloatingTimeLabels
                ? "px-5 pt-6 pb-6"
                : "px-5 pt-3 pb-6"
              : showFloatingTimeLabels
                ? "px-5 pt-8 pb-8"
                : "px-5 pt-5 pb-8"
          }
        >
          <div
            data-slots-container
            ref={slotsContainerRef}
            className={STYLES.slotsContainer}
            role="slider"
            aria-label="Time selection"
            aria-valuenow={0}
            tabIndex={0}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            style={{ cursor: disabled ? "default" : "crosshair" }}
          >
            {showFloatingTimeLabels && selectionRange ? (
              <>
                <div className={STYLES.floatingTimeLabelRow}>
                  {floatingTimeLabelLayout?.mode === "grouped" ? (
                    <div
                      className={STYLES.floatingTimeGrouped}
                      style={{ left: `${floatingTimeLabelLayout.groupLeft}px` }}
                    >
                      <span data-testid="floating-time-label-start">{selectionRange.startTime.toFormat("HH:mm")}</span>
                      <span data-testid="floating-time-label-end">{selectionRange.endTime.toFormat("HH:mm")}</span>
                    </div>
                  ) : floatingTimeLabelLayout?.mode === "separate" ? (
                    <>
                      <span
                        className={STYLES.floatingTimeSeparate}
                        style={{ left: `${floatingTimeLabelLayout.startLeft}px` }}
                        data-testid="floating-time-label-start"
                      >
                        {selectionRange.startTime.toFormat("HH:mm")}
                      </span>
                      <span
                        className={STYLES.floatingTimeSeparate}
                        style={{ left: `${floatingTimeLabelLayout.endLeft}px` }}
                        data-testid="floating-time-label-end"
                      >
                        {selectionRange.endTime.toFormat("HH:mm")}
                      </span>
                    </>
                  ) : null}
                </div>
                <div className={STYLES.floatingTimeMeasure}>
                  <span ref={startLabelMeasureRef}>{selectionRange.startTime.toFormat("HH:mm")}</span>
                  <span ref={endLabelMeasureRef}>{selectionRange.endTime.toFormat("HH:mm")}</span>
                </div>
              </>
            ) : null}
            {slots.map((slot, index) => {
              const isFullHour = slot.minute === 0
              const isFirstSlot = index === 0
              const isLastSlot = index === slots.length - 1
              const hasConflict = conflicts.some((c) => c.equals(slot))
              const color = getSlotColor(slot, hasConflict)
              const opacity = getSlotOpacity(slot)
              const isFirstConflictSlot = !!(conflictRange && index === conflictRange.startIndex)
              const conflictSpan = conflictRange ? conflictRange.endIndex - conflictRange.startIndex + 1 : 0

              return (
                <React.Fragment key={index}>
                  {isFullHour ? <TimeAxis label={slot.toFormat("HH:mm")} isFirst={isFirstSlot} /> : null}
                  <SlotCell
                    index={index}
                    color={color}
                    opacity={opacity}
                    hasMarginRight={!(slot.minute === 45 || isLastSlot)}
                    isFirstConflict={isFirstConflictSlot}
                    conflictSpan={conflictSpan}
                    isScrolling={isScrolling}
                    isDragging={isDragging}
                  />
                </React.Fragment>
              )
            })}
            <TimeAxis label={trailingTime.minute === 0 ? trailingTime.toFormat("HH:mm") : ""} alignRight />
          </div>
        </div>
      </div>
    </div>
  )
}

export default HorizontalSchedule
export type { BusyBlock, CurrentReservation }
